import AsyncStorage from '@react-native-async-storage/async-storage';

import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { resolveSelections } from '@/feature/bet-share-sbk/utils/resolve-selections';
import { BetPosition, BetStatus } from '@/feature/bets-sbk/hooks/types';
import { generateBetsPayload, getGeoToken } from '@/feature/betslip-sbk/utils/bet-submission/generate-bet-payload';
import { delay, pollSubmittedBets } from '@/feature/betslip-sbk/utils/bet-submission/poll-submitted-bets';
import { submitBets } from '@/feature/betslip-sbk/utils/bet-submission/submit-bet';
import { PlaceBetsRequest } from '@/feature/betslip-sbk/utils/bet-submission/types';
import {
    buildConfirmedSubmittedBets,
    generateSgpOddsId,
    getBetOdds,
    getBetSummary,
    groupSelectionIdsByEvent,
    handleOddsUpdateMessages,
    isComboSelectionEnabled,
    mapResolvedSelectionsToOddsUpdates,
} from '@/feature/betslip-sbk/utils/betslip-utils';
import {
    fetchSgpOdds,
    getSgpCombinations,
    handleSgpError,
    handleSgpSuccess,
    hasConflictingSelections,
    revalidateSelectionsAfterRemoval,
} from '@/feature/betslip-sbk/utils/sgp-utils';
import { UserSettings } from '@/hooks/use-auth-user-settings';
import { validateLocationAccuracy } from '@/hooks/use-entries';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { getChannel } from '@/utils/bet-channel';
import { logger } from '@/utils/logging';
import { registerLogoutListener } from '@/utils/logout-listeners';
import { MatchUpdateMessage, OddsUpdateMessageOption } from '@/utils/websocket/types';
import { runStoreUpdate } from '@/utils/zustand';
import { StateCreator, StoreApi, UseBoundStore, create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
    AddSelectionConflictingError,
    AddSelectionMaxSelectionsError,
    BetSlipEvent,
    BetSlipMarket,
    BetSlipOption,
    BetSubmissionOutcome,
    BetSubmissionStatus,
    BetType,
    MAX_SELECTIONS,
    SbkBetSlip,
    SbkBetslipReferrer,
    SbkPromo,
    SelectionParam,
    StakeInputError,
    TotalStakeError,
    UpdateOrFetchNewSgpOddsOptions,
} from '../types';
import {
    autoApplyPromos,
    clearBetSlip,
    clearBetStake,
    enableBetrBucksAndRemovePromo,
    getEventDetails,
    getOddsChangeIndicatorTimeout,
    keepBetSlipSelections,
    keepUnstakedSelections,
    removeSelectedPromo,
    resetBetSlipOnLogout,
    setSelectedPromo,
    toggleComboSelectionStatus,
    updateStake,
    updateStakeInputErrors,
} from '../utils/betslip-actions';
import { addSelection } from '../utils/betslip-actions/add-selection';
import { reEnableLastDisabledSelection, removeSelection } from '../utils/betslip-actions/remove-selection';
import {
    addMultipleSelections,
    addSgpOdds,
    assertMaxSelectionsNotExceeded,
} from '../utils/betslip-add-selections-actions';
import { validBetsSelector } from '../utils/betslip-selectors';
import { withPromoValidation } from '../utils/promo-criteria/promo-criteria-middleware';

export const SBK_BETSLIP_LOG_TAG = '[Sbk Betslip]';
const POLL_INTERVAL_MS = 1000;
const MAX_POLL_RETRIES = 5;

export type SbkBetSlipState = Omit<SbkBetSlip, 'actions'>;

export const initialState: SbkBetSlipState = {
    product: null,
    selections: {},
    events: {},
    markets: {},
    options: {},
    bets: {},
    sgpOdds: {},
    isBetrBucksEnabled: {},
    selectionOrder: [],
    eventOrder: [],
    editBetId: null,
    showKeyboard: false,
    totalStakeErrors: [],
    betSubmissionStatus: BetSubmissionStatus.Idle,
    submittedBets: {},
    submittedState: {
        isCoinsWager: false,
        selections: {},
        events: {},
        markets: {},
        options: {},
        isBetrBucksEnabled: {},
        selectedPromos: {},
        singlesBets: [],
        submittedBets: {}, // We store submitted bets to get access to the global bet ids from PAM
    },
    closedSelections: [],
    oddsChanges: {}, // We store the old odds here to show it in the MultiplierChanges bottom sheet. The format is { selectionId: oldOdds }
    sgpOddsChanges: {},
    sgpEventDisabled: {},
    sgpTemporaryIssue: {},
    userSettings: null,
    oddsChangeTimeout: null,
    producerStatuses: {},
    isSgpFetching: false,
    showComboOddsLoading: false,
    betSubmissionStartTime: null,
    submittedCurrency: null,
    selectedPromos: {},
    isAutoPromoDismissed: false,
    referrer: undefined,
};

export const createSbkBetSlipStore = (
    product: Product.Sweeps | Product.Prediction
): UseBoundStore<StoreApi<SbkBetSlip>> => {
    const stateCreator: StateCreator<SbkBetSlip> = withPromoValidation((set, get) => ({
        ...initialState,
        product,
        actions: {
            addSelection: async (option: BetSlipOption, market: BetSlipMarket, event: BetSlipEvent) => {
                // Predictions entry slip holds a single selection; a new one replaces it
                if (get().product === Product.Prediction) {
                    GASbkEvents.addSelection(market.id, option.id, 1);
                    set(state => {
                        const cleared = clearBetSlip(state);
                        return { ...cleared, ...addSelection(cleared, option, market, event) };
                    });
                    return;
                }

                // check if max selections reached
                const totalSelections = get().selectionOrder.length;
                if (totalSelections === MAX_SELECTIONS) {
                    throw new AddSelectionMaxSelectionsError();
                }

                // add selections
                GASbkEvents.addSelection(market.id, option.id, totalSelections + 1);
                set(state => addSelection(state, option, market, event));

                await get().actions.updateOrFetchNewSgpOdds({ filterEventId: event.id });
            },
            addComboFeaturedBet: async (selections: SelectionParam[]) => {
                const state = get();

                assertMaxSelectionsNotExceeded(state.selectionOrder, selections);

                set({ ...addMultipleSelections(state, selections) });

                GASbkEvents.addFeaturedBet(selections, state.selectionOrder.length + selections.length);

                await state.actions.updateOrFetchNewSgpOdds();
            },
            // replaces the slip rather than appending, and fires no GA add event
            addSharedBetSelections: async (
                selections: SelectionParam[],
                referrer: Omit<SbkBetslipReferrer, 'optionIds'>
            ) => {
                const cleared = clearBetSlip(get());

                assertMaxSelectionsNotExceeded(cleared.selectionOrder, selections);

                // batched so replacing the slip while the betslip is open doesn't crash mid-render
                runStoreUpdate(() => {
                    const added = addMultipleSelections(cleared, selections);
                    set({
                        ...cleared,
                        ...added,
                        referrer: { ...referrer, optionIds: Object.keys(added.selections ?? {}) },
                    });
                });

                await get().actions.updateOrFetchNewSgpOdds();
            },
            addSgpFeaturedBet: async (selections: SelectionParam[], sgpOdds: Record<string, number>) => {
                const state = get();

                assertMaxSelectionsNotExceeded(state.selectionOrder, selections);

                set({
                    ...addMultipleSelections(state, selections),
                    ...addSgpOdds(state.sgpOdds, selections, sgpOdds),
                });

                GASbkEvents.addFeaturedBet(selections, state.selectionOrder.length + selections.length);

                const eventId = selections[0].event.id;
                await state.actions.updateOrFetchNewSgpOdds({ filterEventId: eventId });
            },
            addSgpPlusFeaturedBet: async (selections: SelectionParam[], sgpOdds: Record<string, number>) => {
                const state = get();

                assertMaxSelectionsNotExceeded(state.selectionOrder, selections);

                set({
                    ...addMultipleSelections(state, selections),
                    ...addSgpOdds(state.sgpOdds, selections, sgpOdds),
                });

                GASbkEvents.addFeaturedBet(selections, state.selectionOrder.length + selections.length);

                await state.actions.updateOrFetchNewSgpOdds();
            },
            removeSelection: async (selectionId: string) => {
                // Capture event info before removal
                const eventId = get().selections[selectionId]?.eventId;

                // Synchronous removal (immediate UI update)
                set(state => removeSelection(state, selectionId));

                if (!eventId) {
                    return;
                }

                // Get remaining selections for this event
                const postState = get();
                const allSelectionsForEvent = Object.values(postState.selections).filter(s => s.eventId === eventId);
                const hasDisabled = allSelectionsForEvent.some(s => !s.isComboEnabled);
                const enabledCount = allSelectionsForEvent.filter(s => s.isComboEnabled).length;

                // If no disabled selections, refresh SGP odds for remaining enabled
                if (!hasDisabled) {
                    if (enabledCount > 1) {
                        await get().actions.updateOrFetchNewSgpOdds({ filterEventId: eventId });
                    }
                    return;
                }

                // If all remaining selections are disabled, re-enable the first one
                const reEnabled = reEnableLastDisabledSelection(postState, allSelectionsForEvent);
                if (reEnabled) {
                    set(reEnabled);
                    return;
                }

                // Has disabled selections — re-validate all selections for this event
                const onFetching = () => set({ isSgpFetching: true, showComboOddsLoading: true });
                const result = await revalidateSelectionsAfterRemoval(
                    eventId,
                    allSelectionsForEvent,
                    postState.selectionOrder,
                    onFetching
                );

                // Apply results
                if (result) {
                    set(state => ({
                        selections: { ...state.selections, ...result.selections },
                        sgpOdds: { ...state.sgpOdds, ...result.sgpOdds },
                        sgpEventDisabled: { ...state.sgpEventDisabled, ...result.sgpEventDisabled },
                        sgpTemporaryIssue: { ...state.sgpTemporaryIssue, ...result.sgpTemporaryIssue },
                        isSgpFetching: false,
                        showComboOddsLoading: false,
                    }));
                } else {
                    set({ isSgpFetching: false, showComboOddsLoading: false });
                }
            },
            removeFeaturedBet: async (selectionIds: string[]) => {
                for (const selectionId of selectionIds) {
                    await get().actions.removeSelection(selectionId);
                }
            },
            toggleComboSelectionStatus: (selectionId: string) => {
                set(state => toggleComboSelectionStatus(state, selectionId));
                const eventId = get().selections[selectionId].eventId;
                get().actions.updateOrFetchNewSgpOdds({ filterEventId: eventId });
            },
            toggleMultipleSelectionStatus: (selectionIds: string[]) => {
                const state = get();
                let newState = { ...state };
                selectionIds.forEach(selectionId => {
                    newState = toggleComboSelectionStatus(newState, selectionId);
                });
                set(newState);
            },
            clearReferrer: () => {
                set({ referrer: undefined });
            },
            clearBetSlip: () => {
                set(state => clearBetSlip(state));
            },
            resetOnLogout: () => {
                set(state => resetBetSlipOnLogout(state));
            },
            keepSelections: () => {
                set(state => keepBetSlipSelections(state));
            },
            keepUnstakedSelections: () => {
                set(state => keepUnstakedSelections(state));
            },
            updateStake: (betId: string, stake: number, displayStake: string, betType: BetType) => {
                set(state => updateStake(state, betId, stake, displayStake, betType));
            },
            clearBetStake: (betId: string) => {
                set(state => clearBetStake(state, betId));
            },
            updateUserSettings: (settings: UserSettings) => {
                set({ userSettings: settings });
            },
            setEditingBet: (betId: string | null) => set({ editBetId: betId }),
            toggleUseBetrBucks: (betId: string) => {
                set(state => ({
                    isBetrBucksEnabled: {
                        ...state.isBetrBucksEnabled,
                        [betId]: !state.isBetrBucksEnabled[betId],
                    },
                }));
            },
            updateStakeInputErrors: (betId: string, stakeInputError?: StakeInputError) => {
                set(state => updateStakeInputErrors(state, betId, stakeInputError));
            },
            updateTotalStakeErrors: (errors?: TotalStakeError[]) => {
                set({ totalStakeErrors: errors });
            },
            placeBets: async (userId: string, onFail: (error: unknown) => void) => {
                if (get().betSubmissionStatus === BetSubmissionStatus.Submitting) {
                    logger.warn(SBK_BETSLIP_LOG_TAG, 'Duplicate bet submission prevented');
                    return;
                }

                // pinned here because the geocomply wait can outlast a product or currency switch
                const socialCurrency = useSocialCurrencyStore.getState().activeCurrency;
                set({
                    betSubmissionStatus: BetSubmissionStatus.Submitting,
                    betSubmissionStartTime: new Date().getTime(),
                    submittedCurrency: socialCurrency,
                });

                const state = get();

                const betSettings = {
                    currency: 'USD',
                    channel: getChannel(),
                } as const;

                // Sweeps Coins is the only path that skips GeoComply. Sweeps Cash and
                // Predictions both run the location check; the geocomply client picks the
                // right license per the active product config in Prismic.

                // TODO: adding skip for predictions, check if we should skip geo check for predictions in the future
                const isUsingCoins = socialCurrency === SocialCurrency.COINS;
                const isCoinsWager = state.product === Product.Sweeps && isUsingCoins;
                const skipsGeoCheck = state.product === Product.Prediction || isCoinsWager;

                if (!skipsGeoCheck) {
                    try {
                        await validateLocationAccuracy();
                    } catch (error) {
                        onFail(error);
                        logger.warn(
                            SBK_BETSLIP_LOG_TAG,
                            'Bet submission fail',
                            'Location accuracy validation failed',
                            error
                        );
                        state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                        return;
                    }
                }

                let geotoken: string | undefined;
                if (!skipsGeoCheck) {
                    try {
                        geotoken = await getGeoToken();
                    } catch (error) {
                        onFail(error);
                        logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission fail', 'Failed to generate GeoToken', error);
                        state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                        return;
                    }
                }

                let payload: PlaceBetsRequest;
                try {
                    payload = generateBetsPayload(
                        userId,
                        state,
                        geotoken || 'BYPASS_TOKEN',
                        betSettings,
                        skipsGeoCheck
                    );
                } catch (error: unknown) {
                    onFail(error);
                    logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission fail', 'Failed to generate payload', error);
                    state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                    return;
                }

                try {
                    const resp = await submitBets(payload, { product, socialCurrency });
                    const validSubmittedBets = validBetsSelector(state).filter(bet => !!bet?.stake);
                    /*
                            Since the response of bet submission return in the same order with the payload,
                            we can map the local bet id with the global bet id by using the index of the response data
                        **/
                    const submittedBets = resp.data.reduce(
                        (acc, bet, index) => ({
                            ...acc,
                            [bet.id]: {
                                status: 'UNCONFIRMED',
                                globalId: '',
                                localId: validSubmittedBets[index].id,
                            },
                        }),
                        {}
                    );
                    const singlesBets = validSubmittedBets.filter(bet => bet.betType === 'SINGLE');
                    const comboBet = validSubmittedBets.find(bet => bet.betType === 'COMBO');
                    set({
                        submittedBets,
                        submittedState: {
                            isCoinsWager,
                            selections: state.selections,
                            events: state.events,
                            markets: state.markets,
                            options: state.options,
                            isBetrBucksEnabled: state.isBetrBucksEnabled,
                            selectedPromos: isUsingCoins ? {} : state.selectedPromos,
                            singlesBets,
                            comboBet: comboBet
                                ? {
                                      // Store raw combo odds; the payout calc in
                                      // use-bet-success-data applies the betr bucks
                                      // adjustment once via adjustedPayout (like singles).
                                      odds: getBetOdds(comboBet, state),
                                      selections: Object.values(state.selections).filter(selection =>
                                          isComboSelectionEnabled(selection, state)
                                      ),
                                      bet: comboBet,
                                  }
                                : undefined,
                            submittedBets: {},
                        },
                    });
                } catch (error: unknown) {
                    onFail(error);
                    logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission fail', error, payload);
                    state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                    return;
                }
            },
            updateSubmittedBetStatus: (
                betId: string,
                status: BetStatus,
                globalBetId: string,
                rejectionReason: string,
                onComplete: (outcome: BetSubmissionOutcome) => void,
                position?: BetPosition | null
            ) => {
                const state = get();
                if (betId in state.submittedBets) {
                    const submittedBets = {
                        ...state.submittedBets,
                        [betId]: {
                            ...state.submittedBets[betId],
                            status,
                            globalId: globalBetId,
                            position: position ?? state.submittedBets[betId].position,
                        },
                    };
                    set({ submittedBets, submittedState: { ...state.submittedState, submittedBets } });
                    const allBetsConfirmed = Object.values(submittedBets).every(bet => bet.status === 'CONFIRMED');
                    if (allBetsConfirmed) {
                        const summary = getBetSummary(state, Object.keys(submittedBets).length);
                        state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Success);
                        onComplete({ status: 'confirmed', summary });
                    }

                    const anyBetsRejected = Object.values(submittedBets).some(
                        bet => bet.status === 'REJECTED' || bet.status === 'REFUNDED'
                    );
                    if (anyBetsRejected) {
                        let reason: unknown = rejectionReason;
                        try {
                            reason = JSON.parse(rejectionReason);
                        } catch {
                            // not JSON; log the raw string
                        }
                        logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission rejected', reason);
                        state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                        onComplete({ status: 'rejected' });
                    }
                }
            },
            handleOddsUpdateMessages: (oddsUpdateMessages: OddsUpdateMessageOption[]) => {
                let shouldFetchSgpOdds = false;
                set(state => {
                    const { closedSelections, options, oddsChanges, didStatusChange } = handleOddsUpdateMessages(
                        state,
                        oddsUpdateMessages
                    );
                    shouldFetchSgpOdds = didStatusChange;
                    const timeout = getOddsChangeIndicatorTimeout({ state, oddsChanges });
                    return {
                        ...state,
                        closedSelections,
                        options,
                        oddsChanges,
                        oddsChangeTimeout: timeout,
                    };
                });
                if (shouldFetchSgpOdds) {
                    get().actions.updateOrFetchNewSgpOdds();
                }
            },
            refreshSelectionOdds: async () => {
                const state = get();
                const selectionRefs = Object.values(state.selections).map(selection => ({
                    event_id: selection.eventId,
                    market_id: selection.marketId,
                    option_id: selection.optionId,
                }));
                if (selectionRefs.length === 0) {
                    return;
                }
                const { selections } = await resolveSelections(selectionRefs);
                runStoreUpdate(() =>
                    state.actions.handleOddsUpdateMessages(mapResolvedSelectionsToOddsUpdates(selections))
                );
            },
            handleMatchUpdateMessage: (matchUpdateMessage: MatchUpdateMessage) => {
                set(state => ({
                    events: {
                        ...state.events,
                        [matchUpdateMessage.id]: {
                            ...state.events[matchUpdateMessage.id],
                            event_details: matchUpdateMessage,
                        },
                    },
                }));
            },
            acceptAllOddsChanges: () => {
                set(state => {
                    const { options } = state;
                    const newOptions = { ...options };

                    if (state.oddsChangeTimeout) {
                        clearTimeout(state.oddsChangeTimeout);
                    }

                    // update all options.odds with original odds
                    Object.entries(newOptions).forEach(([optionId, option]) => {
                        if (state.oddsChanges[optionId]) {
                            newOptions[optionId] = {
                                ...option,
                                originalOdds: option.odds,
                            };
                        }
                    });

                    return {
                        ...state,
                        oddsChanges: {},
                        sgpOddsChanges: {},
                        oddsChangeTimeout: null,
                        options,
                    };
                });
            },
            clearOddsChanges: () => {
                set({ oddsChanges: {}, sgpOddsChanges: {} });
            },
            clearHigherOddsChanges: () => {
                const state = get();
                const {
                    oddsChanges,
                    sgpOddsChanges,
                    selectionOrder: selectionIds,
                    selections,
                    options,
                    sgpOdds,
                } = state;
                let newOddsChanges = { ...oddsChanges };
                let newSgpOddsChanges = { ...sgpOddsChanges };
                // Single bets
                selectionIds.forEach(selectionId => {
                    const optionId = selections[selectionId]?.optionId;
                    const currentOdds = options[optionId]?.odds;
                    const previousOdds = oddsChanges[optionId];
                    if (currentOdds && previousOdds && currentOdds > previousOdds) {
                        delete newOddsChanges[selectionId];
                    }
                });

                // SGP/SGP+ bets
                const selectionIdsByEvent = groupSelectionIdsByEvent(state);
                Object.entries(selectionIdsByEvent).forEach(([eventId, ids]) => {
                    const spgId = generateSgpOddsId(ids, eventId);
                    const currentOdds = sgpOdds[spgId];
                    const previousOdds = sgpOddsChanges[spgId];
                    if (currentOdds && previousOdds && currentOdds > previousOdds) {
                        delete newSgpOddsChanges[spgId];
                    }
                });

                set({ oddsChanges: newOddsChanges, sgpOddsChanges: newSgpOddsChanges });
            },
            removeClosedSelections: () => {
                const state = get();
                state.closedSelections.forEach(selection => {
                    state.actions.removeSelection(selection.option.id);
                });
            },
            clearClosedSelections: () => {
                set({ closedSelections: [] });
            },
            updateProducerStatus: (supplier, status) =>
                set(state =>
                    state.producerStatuses[supplier] === status
                        ? state
                        : { producerStatuses: { ...state.producerStatuses, [supplier]: status } }
                ),
            pollSubmittedBets: async onPollFinish => {
                if (get().betSubmissionStatus !== BetSubmissionStatus.Submitting) {
                    logger.debug(SBK_BETSLIP_LOG_TAG, 'Polling exited before starting');
                    return;
                }
                logger.info(SBK_BETSLIP_LOG_TAG, 'Polling started');
                for (let i = 0; i < MAX_POLL_RETRIES; i++) {
                    logger.debug(SBK_BETSLIP_LOG_TAG, `Polling attempt #${i + 1}`);
                    const state = get();
                    if (state.betSubmissionStatus !== BetSubmissionStatus.Submitting) {
                        logger.debug(SBK_BETSLIP_LOG_TAG, 'Polling exited');
                        return; // If bet finished submitting, exit the loop
                    }
                    const { success, betIdMapping } = await pollSubmittedBets(Object.keys(state.submittedBets), {
                        product,
                        socialCurrency: state.submittedCurrency,
                    });
                    if (get().betSubmissionStatus !== BetSubmissionStatus.Submitting) {
                        logger.debug(SBK_BETSLIP_LOG_TAG, 'Polling exited');
                        return;
                    }
                    if (success) {
                        const confirmedSubmittedBets = buildConfirmedSubmittedBets(state.submittedBets, betIdMapping);
                        set({
                            submittedState: { ...get().submittedState, submittedBets: confirmedSubmittedBets },
                        });

                        const summary = getBetSummary(state, Object.keys(betIdMapping).length);
                        state.actions.updateBetSubmissionStatus(BetSubmissionStatus.Success);
                        logger.debug(SBK_BETSLIP_LOG_TAG, 'Polling success');
                        return onPollFinish({ status: 'confirmed', summary });
                    }
                    await delay(POLL_INTERVAL_MS);
                }
                const hasSubmittedIds = Object.keys(get().submittedBets).length > 0;
                logger.warn(SBK_BETSLIP_LOG_TAG, 'Bet submission timeout', { hasSubmittedIds });
                get().actions.updateBetSubmissionStatus(BetSubmissionStatus.Error);
                onPollFinish({ status: hasSubmittedIds ? 'rejected' : 'unconfirmed' });
            },
            updateBetSubmissionStatus: (betSubmissionStatus: 'SUCCESS' | 'ERROR') => {
                set({
                    betSubmissionStatus,
                    submittedBets: {},
                    betSubmissionStartTime: null,
                    submittedCurrency: null,
                });
            },
            updateAllEventDetails: async () => {
                const results = await Promise.allSettled(get().eventOrder.map(getEventDetails));
                set(state => {
                    const newEvents = results.reduce(
                        (acc, result) => {
                            if (result.status === 'fulfilled' && result.value) {
                                const event = result.value;
                                acc[event.id] = {
                                    ...state.events[event.id],
                                    event_details: event.event_details,
                                };
                            }
                            return acc;
                        },
                        { ...state.events }
                    );
                    return { events: newEvents };
                });
            },
            updateOrFetchNewSgpOdds: async ({
                filterEventId, // optional event id to filter SGP combinations
                forceUpdate = false, // Bypasses cache and fetches new odds
                showComboOddsLoading = true,
            }: UpdateOrFetchNewSgpOddsOptions = {}) => {
                let state = get();

                // Get active sgp combinations
                const sgpCombinations = getSgpCombinations(state, filterEventId);

                // start fetching sgp odds concurrently
                const onFetching = () => {
                    set({ isSgpFetching: true, showComboOddsLoading });
                };
                const results = await Promise.allSettled(
                    sgpCombinations.map(sgpSelections => {
                        const eventId = sgpSelections[0].eventId;
                        return fetchSgpOdds(eventId, sgpSelections, forceUpdate, onFetching);
                    })
                );

                state = get();
                let newState = {
                    sgpOdds: state.sgpOdds,
                    sgpEventDisabled: state.sgpEventDisabled,
                    sgpTemporaryIssue: state.sgpTemporaryIssue,
                    selections: state.selections,
                    selectionOrder: state.selectionOrder,
                    oddsChangeTimeout: state.oddsChangeTimeout,
                    sgpOddsChanges: state.sgpOddsChanges,
                };

                // handle results and create new state
                results.forEach((result, index) => {
                    const sgpCombination = sgpCombinations[index];
                    const eventId = sgpCombination[0].eventId;
                    const sgpId = generateSgpOddsId(
                        sgpCombination.map(selection => selection.id),
                        eventId
                    );

                    if (result.status === 'fulfilled') {
                        // handle success
                        const odds = result.value;
                        newState = handleSgpSuccess(sgpId, odds, eventId, newState);

                        // check if odds updated
                        if (state.sgpOdds[sgpId] && state.sgpOdds[sgpId] !== odds) {
                            const previousSgpOdds = state.sgpOdds[sgpId];
                            if (previousSgpOdds) {
                                newState = {
                                    ...newState,
                                    sgpOddsChanges: {
                                        ...newState.sgpOddsChanges,
                                        [sgpId]: previousSgpOdds,
                                    },
                                    oddsChangeTimeout: getOddsChangeIndicatorTimeout({
                                        state,
                                        oddsChanges: state.oddsChanges,
                                    }),
                                };
                            }
                        }
                    } else if (result.status === 'rejected') {
                        // Handle fail
                        const err = result.reason;
                        newState = handleSgpError(err, sgpId, eventId, sgpCombination, newState);
                    }
                });

                // Set the updated state and turn off isSgpFetching
                set({ ...newState, isSgpFetching: false, showComboOddsLoading: false });

                // if any fetches resulted in conflicting selections, throw conflicting error
                if (hasConflictingSelections(results)) {
                    throw new AddSelectionConflictingError();
                }
            },
            handleWsSgpOddsUpdate: (eventId: string) => {
                const state = get();
                if (state.isSgpFetching) {
                    return;
                }
                state.actions.updateOrFetchNewSgpOdds({
                    filterEventId: eventId,
                    forceUpdate: true,
                    showComboOddsLoading: false,
                });
            },
            setSelectedPromo: (betId: string, promo: SbkPromo | null) => {
                set(state => setSelectedPromo(state, betId, promo));
            },
            removeSelectedPromo: (betId: string) => {
                set(state => removeSelectedPromo(state, betId));
            },
            autoApplyPromos: (promosByBetId: Record<string, SbkPromo>) => {
                set(state => autoApplyPromos(state, promosByBetId));
            },
            enableBetrBucksAndRemovePromo: (betId: string) => {
                set(state => enableBetrBucksAndRemovePromo(state, betId));
            },
            clearBetrBucks: () => {
                set({ isBetrBucksEnabled: {} });
            },
        },
    }));

    // Prediction selections are not persisted: the slip is single-selection and clears on close
    if (product === Product.Prediction) {
        return create<SbkBetSlip>()(stateCreator);
    }

    return create<SbkBetSlip>()(
        persist(stateCreator, {
            name: `sbk-betslip-storage-${product}`,
            version: 15,
            storage: createJSONStorage(() => AsyncStorage),
            // producerStatuses is excluded so a fresh session assumes suppliers are up;
            // a persisted DOWN could never be corrected (rewind=1 only replays the last message)
            // isBetrBucksEnabled is excluded because the currency store is not persisted and boots
            // in coins, where the bonus toggle is never offered
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            partialize: ({ actions, producerStatuses, isBetrBucksEnabled, ...rest }: SbkBetSlip) => rest,
        })
    );
};

export type BetSlipProduct = Product.Sweeps | Product.Prediction;

const storeInstances: Record<BetSlipProduct, ReturnType<typeof createSbkBetSlipStore>> = {
    [Product.Sweeps]: createSbkBetSlipStore(Product.Sweeps),
    [Product.Prediction]: createSbkBetSlipStore(Product.Prediction),
};

registerLogoutListener(() => {
    Object.values(storeInstances).forEach(store => store.getState().actions.resetOnLogout());
});

// The bonus toggle is only offered in cash, so a bet must not stay bonus-enabled across a currency switch
useSocialCurrencyStore.subscribe((state, prevState) => {
    if (state.activeCurrency !== prevState.activeCurrency) {
        storeInstances[Product.Sweeps].getState().actions.clearBetrBucks();
    }
});

const resolveBetSlipProduct = (product: Product): BetSlipProduct =>
    product === Product.Prediction ? Product.Prediction : Product.Sweeps;

export const useSbkBetSlipStore = <T>(selector: (state: SbkBetSlip) => T): T => {
    const product = useJurisdictionStore(state => state.product);
    return storeInstances[resolveBetSlipProduct(product)](selector);
};

export const getSbkBetSlipStore = (product: BetSlipProduct) => storeInstances[product];

export const getActiveSbkBetSlipStore = () =>
    storeInstances[resolveBetSlipProduct(useJurisdictionStore.getState().product)];
